import {
  CHANGE_HISTORY_PAGE_SIZES,
  type ManagedGroupHistoryChange,
  type ManagedGroupHistoryItem,
  type ManagedGroupHistoryPolicy,
} from "@adpilot/shared";
import { IconChevronRight } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Fragment, type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fetchCampaignManagedGroupHistory } from "@/lib/campaign-api";
import { useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";

const COLUMNS = ["objectType", "content", "operator", "occurredAt"] as const;
const POLICY_ORDER = ["targetAcos", "businessGoal", "riskPreference", "executionMode"] as const;

export interface ManagedGroupHistoryPanelProps {
  campaignId: string;
  /** Formats an instant the way the main log does. */
  formatTime: (iso: string) => string;
}

/** 托管组操作日志 — collapsed by default and fetched on first open, as in the reference. */
export function ManagedGroupHistoryPanel({
  campaignId,
  formatTime,
}: ManagedGroupHistoryPanelProps) {
  const { t } = useTranslation(["campaign", "managedGroups"]);
  const text = (key: string, options?: Record<string, unknown>) =>
    t(`campaign:detail.changeHistory.managedGroups.${key}`, options);
  const { formatPercent } = useFormatters();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(200);

  const query = useQuery({
    queryKey: ["campaign", campaignId, "managed-group-history", page, pageSize],
    queryFn: ({ signal }) =>
      fetchCampaignManagedGroupHistory(campaignId, { page, pageSize }, signal),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    enabled: open,
  });
  const items = query.data?.data.items ?? [];
  const total = query.data?.pagination.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  // A refetch can shrink the history below the current page.
  useEffect(() => {
    if (query.data && page > pageCount) setPage(pageCount);
  }, [query.data, page, pageCount]);

  const groupLink = (group: { id: string; name: string }) => (
    <Link
      to="/managed-groups/$id"
      params={{ id: group.id }}
      className="text-primary hover:underline"
    >
      【{group.name}】
    </Link>
  );

  const policyValue = (field: (typeof POLICY_ORDER)[number] | "name", value: unknown): string => {
    switch (field) {
      case "targetAcos":
        return formatPercent(value as number, 2);
      case "businessGoal":
        return t(`managedGroups:policy.goals.${value}`);
      case "riskPreference":
        return t(`managedGroups:policy.risks.${value}`);
      case "executionMode":
        return t(`managedGroups:policy.modes.${value}`);
      default:
        return String(value);
    }
  };
  const fieldLabel = (field: (typeof POLICY_ORDER)[number] | "name") =>
    field === "name" ? text("name") : t(`managedGroups:policy.${field}`);

  const policySentence = (policy: ManagedGroupHistoryPolicy | null) =>
    POLICY_ORDER.flatMap((field) =>
      policy?.[field] === undefined
        ? []
        : [
            text("policySet", {
              field: fieldLabel(field),
              value: policyValue(field, policy[field]),
            }),
          ],
    ).join("");
  const changeSentence = (changes: ManagedGroupHistoryChange[]) =>
    changes
      .map((change) =>
        text("changed", {
          field: fieldLabel(change.field),
          from: policyValue(change.field, change.from),
          to: policyValue(change.field, change.to),
        }),
      )
      .join("");

  /** The action sentence with group names as links; `[group]` / `[from]` mark where. */
  const content = (item: ManagedGroupHistoryItem): ReactNode => {
    const template = text(`actions.${item.action}`);
    const parts = template.split(/(\[group\]|\[from\])/);
    const tail =
      item.action === "added"
        ? policySentence(item.policy)
        : item.action === "group_updated"
          ? changeSentence(item.changes)
          : "";
    // Keyed by each piece's offset in the template: stable, and unique per row.
    let offset = 0;
    const pieces = parts.map((part) => {
      const key = offset;
      offset += part.length;
      return { key, part };
    });
    return (
      <>
        {pieces.map(({ key, part }) => (
          <Fragment key={key}>
            {part === "[group]"
              ? groupLink(item.group)
              : part === "[from]"
                ? item.fromGroup && groupLink(item.fromGroup)
                : part}
          </Fragment>
        ))}
        {tail}
      </>
    );
  };

  const body = query.isPending ? (
    <div className="flex flex-col gap-2 p-3.5" role="status" aria-label={text("loading")}>
      {[1, 2, 3].map((n) => (
        <Skeleton key={n} className="h-8 w-full" />
      ))}
    </div>
  ) : query.isError && !query.data ? (
    <div className="p-3.5">
      <ErrorState
        message={text("loadFailed")}
        onRetry={() => void query.refetch()}
        isRetrying={query.isFetching}
      />
    </div>
  ) : items.length === 0 ? (
    <div className="p-3.5">
      <EmptyState title={text("empty")} description={text("emptyDescription")} />
    </div>
  ) : (
    <DataTable density="compact" aria-label={text("title")}>
      <TableHeader>
        <TableRow>
          {COLUMNS.map((column) => (
            <TableHead key={column} className="whitespace-nowrap">
              {text(`columns.${column}`)}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.id} className="hover:bg-table-row-hover">
            <TableCell className="whitespace-nowrap">{text("objectType")}</TableCell>
            <TableCell className="whitespace-normal leading-5">{content(item)}</TableCell>
            <TableCell className="whitespace-nowrap">
              {item.operatorEmail ?? text("system")}
            </TableCell>
            <TableCell className="whitespace-nowrap font-mono tabular-nums">
              {formatTime(item.occurredAt)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </DataTable>
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <section
        aria-label={text("title")}
        className="overflow-hidden rounded-[14px] border border-line bg-surface"
      >
        <CollapsibleTrigger className="flex w-full items-center gap-1.5 px-3.5 py-2.5 text-left text-[13px] font-semibold text-ink">
          <IconChevronRight
            aria-hidden="true"
            className={cn("size-4 transition-transform", open && "rotate-90")}
          />
          {text("title")}
        </CollapsibleTrigger>
        {/* Rows load after opening, so the panel follows its content's height. */}
        <CollapsiblePanel className="h-auto">
          <div className="border-t border-line" aria-busy={query.isFetching}>
            <DataTableFrame
              className="rounded-none border-0"
              footer={
                <DataTablePagination
                  page={page}
                  pageCount={pageCount}
                  pageSize={pageSize}
                  pageSizeOptions={CHANGE_HISTORY_PAGE_SIZES}
                  summary={t("campaign:detail.changeHistory.count", { count: total })}
                  onPageChange={setPage}
                  onPageSizeChange={(next) => {
                    setPageSize(next);
                    setPage(1);
                  }}
                />
              }
            >
              {body}
            </DataTableFrame>
          </div>
        </CollapsiblePanel>
      </section>
    </Collapsible>
  );
}
